import { zipSync } from 'fflate';
import type {
  CourseData,
  DeckIR,
  FidelityReport,
  ParseDiagnostic,
  PlayerCapabilities,
  PublishedCourse,
} from '@slideforge/shared';
import {
  applyCompileDrops,
  assertFidelityReportValid,
  canPublish,
  textBodyToPlainText,
  totalPoints,
  validateInteraction,
  walkShapes,
} from '@slideforge/shared';
import { compileCourse } from './compile-course.js';
import { indexHtml } from './html.js';
import {
  cmi5Xml,
  scorm12Manifest,
  scorm2004Manifest,
  tincanXml,
  type ScormSequencing,
} from './manifests.js';

export type PublishTarget = 'html5' | 'html5-single-file' | 'scorm12' | 'scorm2004' | 'xapi' | 'cmi5';

export interface PublishStageEvent {
  readonly stage:
    | 'compiling'
    | 'checking-fidelity'
    | 'writing-course'
    | 'copying-player'
    | 'copying-media'
    | 'generating-manifest'
    | 'zipping'
    | 'done';
  /** 0-1. */
  readonly progress: number;
}

export interface PublishOptions {
  readonly deck: DeckIR;
  readonly course: CourseData;
  readonly diagnostics: readonly ParseDiagnostic[];
  readonly fidelity: FidelityReport;
  readonly target: PublishTarget;
  /** Bytes for each media asset, keyed by content hash. */
  readonly mediaBytes: ReadonlyMap<string, Uint8Array>;
  /**
   * Bytes for each narration clip, keyed by the audio store's content hash.
   * Absent clips are reported rather than skipped: a course that publishes
   * silently without the narration the author recorded is the worse failure.
   */
  readonly narrationBytes?: ReadonlyMap<string, Uint8Array>;
  /** The built player runtime (`packages/player/dist/player.js`). */
  readonly playerScript: string;
  readonly generatorVersion?: string;
  /** Pinned by the caller; the only field allowed to vary between two builds. */
  readonly publishedAt?: string;
  readonly sequencing?: ScormSequencing;
  readonly onProgress?: (event: PublishStageEvent) => void;
  readonly signal?: AbortSignal;
}

export interface PublishResult {
  readonly zip: Uint8Array;
  readonly files: ReadonlyMap<string, Uint8Array>;
  readonly course: PublishedCourse;
  readonly fidelity: FidelityReport;
  readonly entryPoint: string;
  readonly target: PublishTarget;
}

/**
 * Raised when a slide claims narration whose audio is not available.
 *
 * Recordings live in the add-in's local audio store, so a deck edited on one
 * machine and published on another can reference clips that were never carried
 * across. Publishing a silent course in that situation looks like success and
 * is discovered by a student.
 */
export class NarrationMissing extends Error {
  constructor(readonly audioIds: readonly string[]) {
    super(
      `The audio for ${audioIds.length} narration clip(s) is not on this computer, so the course would publish silent. ` +
        'Recordings are stored locally; publish from the machine they were recorded on, or re-record them here.',
    );
    this.name = 'NarrationMissing';
  }
}

/** TTS audio whose source text changed is never allowed into a learner package. */
export class NarrationStale extends Error {
  constructor(readonly slideIds: readonly string[]) {
    super(
      `Synthetic narration on ${slideIds.length} slide(s) no longer matches its notes or script. ` +
        'Regenerate the named slides before publishing.',
    );
    this.name = 'NarrationStale';
  }
}

export class PublishBlocked extends Error {
  constructor(readonly blocking: readonly ParseDiagnostic[]) {
    super(
      `This deck cannot be published yet:\n${blocking.map((d) => `  • ${d.message}`).join('\n')}`,
    );
    this.name = 'PublishBlocked';
  }
}

export class InteractionInvalid extends Error {
  constructor(readonly slides: readonly string[]) {
    super(`Interactions on these slides must be fixed before publishing: ${slides.join(', ')}`);
    this.name = 'InteractionInvalid';
  }
}

export class PublishCancelled extends Error {
  constructor() {
    super('Publishing was cancelled.');
    this.name = 'PublishCancelled';
  }
}

const TRACKING_BY_TARGET: Readonly<Record<PublishTarget, PlayerCapabilities['tracking']>> = {
  html5: 'none',
  'html5-single-file': 'none',
  scorm12: 'scorm12',
  scorm2004: 'scorm2004',
  xapi: 'xapi',
  cmi5: 'cmi5',
};

/**
 * Builds a distributable package.
 *
 * Deterministic by design (AGENTS.md): the same deck and course data produce
 * byte-identical output except `publishedAt`. That is what makes "did anything
 * actually change in this course?" answerable by comparing two zips.
 */
export async function publish(options: PublishOptions): Promise<PublishResult> {
  const emit = (stage: PublishStageEvent['stage'], progress: number) => {
    checkCancelled(options.signal);
    options.onProgress?.({ stage, progress });
  };

  const publishedAt = options.publishedAt ?? new Date().toISOString();
  const generatorVersion = options.generatorVersion ?? '0.1.0';
  const inlineCourse = options.target === 'html5-single-file';
  const tracking = TRACKING_BY_TARGET[options.target];

  emit('compiling', 0.05);
  await assertNarrationFresh(options.deck, options.course);
  assertInteractionsValid(options.course);
  const compiled = compileCourse({
    deck: options.deck,
    course: options.course,
    diagnostics: options.diagnostics,
    capabilities: {
      tracking,
      inlineCourse,
      allowPrint: Object.values(options.course.slides).some(
        (ns) => ns.quiz?.resultSlide.allowPrint === true,
      ),
    },
    generatorVersion,
    publishedAt,
    fidelity: options.fidelity,
  });

  emit('checking-fidelity', 0.15);
  // The parser's own report is merged with what the render compiler had to drop,
  // so a construct lost at layout time is not silently absent from the report.
  const fidelity = applyCompileDrops(options.fidelity, compiled.droppedBySlideIndex);
  assertFidelityReportValid(fidelity);
  if (!canPublish(fidelity)) {
    throw new PublishBlocked(fidelity.blocking);
  }

  const course: PublishedCourse = { ...compiled.course, fidelity };
  const files = new Map<string, Uint8Array>();
  const encoder = new TextEncoder();
  const put = (name: string, content: string | Uint8Array) => {
    files.set(name, typeof content === 'string' ? encoder.encode(content) : content);
  };

  emit('writing-course', 0.3);
  if (!inlineCourse) {
    put('course.json', JSON.stringify(course, null, 2));
  }
  // The report ships with the package so "what did we lose" is answerable six
  // months later without re-parsing the deck.
  put('fidelity-report.json', JSON.stringify(fidelity, null, 2));

  emit('copying-player', 0.45);
  put('player.js', options.playerScript);

  emit('copying-media', 0.6);
  for (const asset of compiled.usedMedia) {
    const bytes = options.mediaBytes.get(asset.id);
    if (bytes) put(`media/${asset.id}.${asset.extension}`, bytes);
  }

  const missingNarration: string[] = [];
  for (const [audioId, clip] of compiled.usedNarration) {
    const bytes = options.narrationBytes?.get(audioId);
    if (bytes) put(clip.href, bytes);
    else missingNarration.push(audioId);
    if (clip.captionsHref && clip.captions) {
      put(clip.captionsHref, captionsToWebVtt(clip.captions));
    }
  }
  if (missingNarration.length > 0) {
    throw new NarrationMissing(missingNarration);
  }

  put(
    'index.html',
    indexHtml({
      course,
      inlineCourse,
      playerScriptHref: 'player.js',
      courseJsonHref: 'course.json',
    }),
  );

  emit('generating-manifest', 0.75);
  const manifestOptions = {
    course,
    files: [...files.keys()].sort(),
    entryPoint: 'index.html',
    ...(options.sequencing ? { sequencing: options.sequencing } : {}),
    ...(masteryScoreOf(options.course) !== undefined
      ? { masteryScore: masteryScoreOf(options.course) as number }
      : {}),
  };

  switch (options.target) {
    case 'scorm12':
      put('imsmanifest.xml', scorm12Manifest(manifestOptions));
      break;
    case 'scorm2004':
      put('imsmanifest.xml', scorm2004Manifest(manifestOptions));
      break;
    case 'xapi':
      put('tincan.xml', tincanXml(manifestOptions));
      break;
    case 'cmi5':
      put('cmi5.xml', cmi5Xml(manifestOptions));
      break;
    case 'html5':
    case 'html5-single-file':
      break;
  }

  emit('zipping', 0.9);
  const zip = zipDeterministically(files);

  emit('done', 1);
  return { zip, files, course, fidelity, entryPoint: 'index.html', target: options.target };
}

function assertInteractionsValid(course: CourseData): void {
  const invalid: string[] = [];
  for (const [slideId, namespaces] of Object.entries(course.slides)) {
    if (namespaces.interaction && validateInteraction(namespaces.interaction).length > 0) invalid.push(slideId);
  }
  if (invalid.length > 0) throw new InteractionInvalid(invalid);
}

async function assertNarrationFresh(deck: DeckIR, course: CourseData): Promise<void> {
  const stale: string[] = [];
  for (const slide of deck.slides) {
    const track = course.slides[slide.id]?.narration;
    const active = track ? track.takes.find((take) => take.id === track.activeTakeId) : undefined;
    if (active?.source !== 'tts') continue;
    const tts = track?.tts;
    const text = tts?.textSource === 'script'
      ? tts.script
      : tts?.textSource === 'body'
        ? slideBodyText(slide.shapes)
        : slide.notes
          ? textBodyToPlainText(slide.notes)
          : '';
    const actual = await sha256Text(text);
    const expectedSynthesisKey = tts
      ? await ttsSynthesisKey({
          text,
          voiceId: tts.voiceId || course.tts.voiceId,
          modelId: tts.modelId ?? course.tts.modelId,
          locale: course.language,
          rate: course.tts.rate,
          pitch: course.tts.pitch,
          volume: course.tts.volume,
          pauseAfterSentenceMs: course.tts.pauseAfterSentenceMs,
          lexiconVersion: course.tts.lexiconVersion,
          pronunciationDictionaryLocators: course.tts.pronunciationDictionary
            ? [course.tts.pronunciationDictionary]
            : [],
        })
      : undefined;
    if (
      !tts?.sourceHash ||
      tts.sourceHash !== actual ||
      !expectedSynthesisKey ||
      active.synthesisKey !== expectedSynthesisKey ||
      tts.synthesisKey !== expectedSynthesisKey
    ) {
      stale.push(slide.id);
    }
  }
  if (stale.length > 0) throw new NarrationStale(stale);
}

async function sha256Text(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function ttsSynthesisKey(request: {
  readonly text: string;
  readonly voiceId: string;
  readonly modelId: string;
  readonly locale: string;
  readonly rate: number;
  readonly pitch: number;
  readonly volume: number;
  readonly pauseAfterSentenceMs: number;
  readonly lexiconVersion: number;
  readonly pronunciationDictionaryLocators: readonly { readonly id: string; readonly versionId: string }[];
}): Promise<string> {
  return sha256Text(JSON.stringify({
    text: request.text,
    voiceId: request.voiceId,
    modelId: request.modelId,
    locale: request.locale,
    rate: request.rate,
    pitch: request.pitch,
    volume: request.volume,
    pauseAfterSentenceMs: request.pauseAfterSentenceMs,
    lexiconVersion: request.lexiconVersion,
    outputFormat: 'mp3_44100_128',
    pronunciationDictionaryLocators: [...request.pronunciationDictionaryLocators]
      .sort((a, b) => `${a.id}:${a.versionId}`.localeCompare(`${b.id}:${b.versionId}`)),
  }));
}

function slideBodyText(shapes: DeckIR['slides'][number]['shapes']): string {
  const blocks: string[] = [];
  for (const shape of walkShapes(shapes)) {
    if ((shape.kind === 'shape' || shape.kind === 'graphicFrame') && shape.text) {
      blocks.push(textBodyToPlainText(shape.text));
    } else if (shape.kind === 'table') {
      for (const row of shape.rows) for (const cell of row.cells) blocks.push(textBodyToPlainText(cell.text));
    }
  }
  return blocks.filter(Boolean).join('\n');
}

/**
 * Zips with a pinned timestamp and sorted entries.
 *
 * Without both, two builds of an unchanged deck differ in every local file
 * header, and the determinism promise is worth nothing.
 */
export function zipDeterministically(files: ReadonlyMap<string, Uint8Array>): Uint8Array {
  const ordered: Record<string, Uint8Array> = {};
  for (const name of [...files.keys()].sort()) {
    const bytes = files.get(name);
    if (bytes) ordered[name] = bytes;
  }
  return zipSync(ordered, { level: 6, mtime: new Date('2026-01-01T00:00:00Z') });
}

/**
 * The pass mark the LMS should apply, as a 0-1 fraction.
 *
 * Averaged across quizzes weighted by points, because the package is a single
 * SCO: an LMS has one mastery score to work with however many quizzes the deck
 * contains.
 */
export function masteryScoreOf(course: CourseData): number | undefined {
  const quizzes = Object.values(course.slides)
    .map((ns) => ns.quiz)
    .filter((q): q is NonNullable<typeof q> => q !== undefined && q.kind === 'Graded');
  if (quizzes.length === 0) return undefined;

  const weighted = quizzes.reduce(
    (acc, quiz) => {
      const points = totalPoints(quiz);
      return { points: acc.points + points, score: acc.score + (quiz.passingScore / 100) * points };
    },
    { points: 0, score: 0 },
  );
  if (weighted.points === 0) return undefined;
  return Math.round((weighted.score / weighted.points) * 10000) / 10000;
}

function checkCancelled(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new PublishCancelled();
}

function captionsToWebVtt(
  cues: readonly { readonly startMs: number; readonly endMs: number; readonly text: string }[],
): string {
  const body = cues.map((cue, index) =>
    `${index + 1}\n${webVttTimestamp(cue.startMs)} --> ${webVttTimestamp(cue.endMs)}\n${cue.text}`,
  );
  return `WEBVTT\n\n${body.join('\n\n')}\n`;
}

function webVttTimestamp(milliseconds: number): string {
  const value = Math.max(0, Math.round(milliseconds));
  const hours = Math.floor(value / 3_600_000);
  const minutes = Math.floor((value % 3_600_000) / 60_000);
  const seconds = Math.floor((value % 60_000) / 1_000);
  const millis = value % 1_000;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}
